import { UserData } from '../utils/types'
import {
  ChainSummary,
  LenderDataEntry,
  LenderSummary,
  PortfolioSummary,
  SubAccountSummary,
  SummaryBalanceData,
  UserDataResult,
} from './types'
import {
  type AprWeightItem,
  calculateLeverage,
  calculateWeightedAprs,
} from './utils'

/** Tiebreaker priority: AAVE_V3 variants first, COMPOUND_V3 variants second, rest last */
function lenderSortPriority(lender: string): number {
  if (/^AAVE_V3/i.test(lender)) return 0
  if (/^COMPOUND_V3/i.test(lender)) return 1
  return 2
}

/**
 * Aggregates a single lender's user data into a summary
 */
function aggregateLenderData(
  lender: string,
  userData: UserData,
): LenderSummary {
  const { chainId, data } = userData

  let totalDeposits = 0
  let totalDebt = 0
  let totalCollateral = 0
  let totalCollateralAllActive = 0
  let totalNav24h = 0
  let totalDeposits24h = 0
  let totalDebt24h = 0
  const subAccountSummaries: SubAccountSummary[] = []
  const aprItems: AprWeightItem[] = []

  for (const subAccount of data) {
    const { balanceData, aprData, positions, health, accountId, userConfig } =
      subAccount

    totalDeposits += balanceData.deposits
    totalDebt += balanceData.debt
    totalCollateral += balanceData.collateral
    totalCollateralAllActive += balanceData.collateralAllActive
    totalNav24h += balanceData.nav24h ?? 0
    totalDeposits24h += balanceData.deposits24h ?? 0
    totalDebt24h += balanceData.debt24h ?? 0

    // Collect APR data with balance weights for weighted averaging
    aprItems.push({
      deposits: balanceData.deposits,
      debt: balanceData.debt,
      nav: balanceData.deposits - balanceData.debt,
      apr: aprData.apr,
      depositApr: aprData.depositApr,
      borrowApr: aprData.borrowApr,
      rewardApr: aprData.rewardApr,
      rewardDepositApr: aprData.rewardDepositApr,
      rewardBorrowApr: aprData.rewardBorrowApr,
      intrinsicApr: aprData.intrinsicApr,
      intrinsicDepositApr: aprData.intrinsicDepositApr,
      intrinsicBorrowApr: aprData.intrinsicBorrowApr,
    })

    // Sub-account summary
    subAccountSummaries.push({
      accountId,
      health,
      balanceData,
      aprData,
      userConfig,
      positionCount: positions.length,
    })
  }

  const nav = totalDeposits - totalDebt
  const leverage = calculateLeverage(totalDeposits, nav)
  const weightedAprs = calculateWeightedAprs(aprItems)

  const balanceData: SummaryBalanceData = {
    deposits: totalDeposits,
    debt: totalDebt,
    collateral: totalCollateral,
    collateralAllActive: totalCollateralAllActive,
    nav,
    deposits24h: totalDeposits24h,
    debt24h: totalDebt24h,
    nav24h: totalNav24h,
  }

  return {
    lender,
    chainId,
    balanceData,
    aprData: {
      ...weightedAprs,
      rewards: {},
    },
    leverage,
    subAccounts: subAccountSummaries,
  }
}

/**
 * Builds portfolio summaries from user data results
 *
 * @param userDataResult - The result from convertLenderUserDataResult or getLenderUserDataMulti
 * @returns PortfolioSummary with aggregated data across all lenders and chains
 *
 * @example
 * ```typescript
 * const userData = await getLenderUserDataMulti(account, chainQueries, lenderState)
 * const summary = buildSummaries(userData)
 *
 * console.log(`Total net worth: $${summary.balanceData.nav.toFixed(2)}`)
 * console.log(`Overall leverage: ${summary.leverage.toFixed(2)}x`)
 * console.log(`Net APR: ${summary.aprData.apr.toFixed(2)}%`)
 * ```
 */
export function buildSummaries(
  userDataResult: UserDataResult,
): PortfolioSummary {
  const lenderSummaries: LenderSummary[] = []
  const chainMap: { [chainId: string]: ChainSummary } = {}

  let totalDeposits = 0
  let totalDebt = 0
  let totalNav24h = 0
  let totalDeposits24h = 0
  let totalDebt24h = 0
  let totalCollateral = 0
  let totalCollateralAllActive = 0
  const aprItems: AprWeightItem[] = []

  const activeChains = new Set<string>()

  // Process each chain
  for (const [chainId, lenders] of Object.entries(userDataResult)) {
    activeChains.add(chainId)

    if (!chainMap[chainId]) {
      chainMap[chainId] = {
        chainId,
        totalDepositsUSD: 0,
        totalDebtUSD: 0,
        netWorth: 0,
        lenderCount: 0,
      }
    }

    // Process each lender on this chain
    for (const [lender, userData] of Object.entries(lenders)) {
      const summary = aggregateLenderData(lender, userData)

      lenderSummaries.push(summary)

      // Accumulate balance totals
      totalDeposits += summary.balanceData.deposits
      totalDebt += summary.balanceData.debt
      totalNav24h += summary.balanceData.nav24h
      totalDeposits24h += summary.balanceData.deposits24h
      totalDebt24h += summary.balanceData.debt24h
      totalCollateral += summary.balanceData.collateral
      totalCollateralAllActive += summary.balanceData.collateralAllActive

      // Collect lender-level APR data with balance weights for weighted averaging
      aprItems.push({
        deposits: summary.balanceData.deposits,
        debt: summary.balanceData.debt,
        nav: summary.balanceData.nav,
        apr: summary.aprData.apr,
        depositApr: summary.aprData.depositApr,
        borrowApr: summary.aprData.borrowApr,
        rewardApr: summary.aprData.rewardApr,
        rewardDepositApr: summary.aprData.rewardDepositApr,
        rewardBorrowApr: summary.aprData.rewardBorrowApr,
        intrinsicApr: summary.aprData.intrinsicApr,
        intrinsicDepositApr: summary.aprData.intrinsicDepositApr,
        intrinsicBorrowApr: summary.aprData.intrinsicBorrowApr,
      })

      // Update chain summary
      chainMap[chainId].totalDepositsUSD += summary.balanceData.deposits
      chainMap[chainId].totalDebtUSD += summary.balanceData.debt
      chainMap[chainId].lenderCount++
    }

    // Calculate chain net worth
    chainMap[chainId].netWorth =
      chainMap[chainId].totalDepositsUSD - chainMap[chainId].totalDebtUSD
  }

  const totalNav = totalDeposits - totalDebt
  const leverage = calculateLeverage(totalDeposits, totalNav)
  const weightedAprs = calculateWeightedAprs(aprItems)

  // Sort lenders by nav descending, then by protocol priority for determinism
  lenderSummaries.sort(
    (a, b) =>
      b.balanceData.nav - a.balanceData.nav ||
      lenderSortPriority(a.lender) - lenderSortPriority(b.lender) ||
      a.lender.localeCompare(b.lender),
  )

  return {
    balanceData: {
      deposits: totalDeposits,
      debt: totalDebt,
      collateral: totalCollateral,
      collateralAllActive: totalCollateralAllActive,
      nav: totalNav,
      deposits24h: totalDeposits24h,
      debt24h: totalDebt24h,
      nav24h: totalNav24h,
    },
    aprData: {
      ...weightedAprs,
      rewards: {},
    },
    leverage,
    activeLenders: lenderSummaries.length,
    activeChains: activeChains.size,
    lenders: lenderSummaries,
    chains: Object.values(chainMap),
  }
}

/**
 * Filters portfolio summary to only include lenders with non-zero positions
 */
export function filterActiveLenders(
  summary: PortfolioSummary,
): PortfolioSummary {
  const activeLenders = summary.lenders.filter(
    (l) => l.balanceData.deposits > 0 || l.balanceData.debt > 0,
  )

  return {
    ...summary,
    lenders: activeLenders,
    activeLenders: activeLenders.length,
  }
}

/**
 * Fuses UserData entries with their LenderSummary counterparts into
 * a flat array of LenderDataEntry objects, sorted by nav descending.
 */
export function fuseLenderData(
  userDataResult: UserDataResult,
  summary: PortfolioSummary,
): LenderDataEntry[] {
  const entries: LenderDataEntry[] = []

  for (const lenderSummary of summary.lenders) {
    const { lender, chainId, subAccounts: _, ...summaryFields } = lenderSummary
    const userData = userDataResult[chainId]?.[lender]
    if (!userData) continue

    entries.push({
      ...summaryFields,
      lender,
      chainId,
      account: userData.account,
      data: userData.data,
      // Carry the partial-read marker onto the entry the client renders — the
      // top-level `incompleteLenders` list alone leaves a consumer to re-join it
      // by key, which no client did, so broken aggregates rendered as fact.
      ...(userData.incomplete ? { incomplete: true } : {}),
      // Same reasoning for the stale marker: a client that cannot see it will
      // render a cached position as a live one.
      ...(userData.stale
        ? { stale: true, staleAgeMs: userData.staleAgeMs }
        : {}),
    })
  }

  // Already sorted by buildSummaries, but ensure order
  entries.sort(
    (a, b) =>
      b.balanceData.nav - a.balanceData.nav ||
      lenderSortPriority(a.lender) - lenderSortPriority(b.lender) ||
      a.lender.localeCompare(b.lender),
  )
  return entries
}
